import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import FeatherIcon from '../../common/FeatherIcon';
import useModuleLicense from '../../../licensing/useModuleLicense';
import { isNavPathLocked } from '../../../licensing/navModules';
import { readNormalizedUserFromStorage } from '../../../utils/entitlements';
import { isFeatureEnabled } from '../../../utils/featureFlags';
import { canViewBilling, isPrivilegedUser } from '../../../utils/permissions';
import { hasCapability } from '../../../pages/custom-agents/customAgentsModule';
import { buildCommandIndex, searchCommands } from './commandIndex';
import './CommandBar.css';

/**
 * The ⌘K / Ctrl+K command bar (platform-restructure-spec v2 §5.5, MVP): type a
 * page's name, or a word people use for it, and Enter opens it. Part of "New
 * navigation" — the header mounts it only while that switch is on. It has no
 * button in the header (2026-10-26: no search bar on top); the shortcut is
 * the way in, and closing returns focus to wherever it was.
 *
 * /global-search keeps ⌘K for its own search box, so the shortcut stands down
 * there.
 */
const CommandBar = () => {
  const { t } = useTranslation(['nav', 'layout', 'agentAtlas', 'securityAtlas']);
  const navigate = useNavigate();
  const location = useLocation();
  const license = useModuleLicense();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const returnFocusRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || String(e.key).toLowerCase() !== 'k') return;
      if (location.pathname.startsWith('/global-search')) return;
      e.preventDefault();
      setOpen((o) => {
        if (!o) returnFocusRef.current = document.activeElement;
        return !o;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    inputRef.current?.focus();
  }, [open]);

  // Built when the bar opens, so a role or licence change since is picked up.
  const entries = useMemo(() => {
    if (!open) return [];
    const user = readNormalizedUserFromStorage();
    const orgId = Number(user.organization_id ?? user.org_id ?? user.organizationId);
    const isAdmin = isPrivilegedUser(user);
    return buildCommandIndex({
      t,
      gates: { canGovern: true, orgId, featureOn: isFeatureEnabled },
      isAdmin,
      canBill: canViewBilling(),
      // The classic module rows' gates, as the sidebar evaluates them (Sidebar.jsx
      // canOpenCustomModule / canOpenCustomGovernance / canOpenCustomAlerts are open).
      moduleCtx: { can: () => true, cap: hasCapability, isAdmin, canGov: true, canAlerts: true, featureOn: isFeatureEnabled },
      isLocked: (to) => isNavPathLocked(to, license),
    });
  }, [open, t, license]);
  const results = useMemo(() => searchCommands(entries, query), [entries, query]);

  const close = () => {
    setOpen(false);
    returnFocusRef.current?.focus?.();
  };

  const go = (entry) => {
    setOpen(false);
    navigate(entry.to);
  };

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[active]) go(results[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const optionId = (i) => `cmdk-option-${i}`;

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="cmdk-backdrop" onMouseDown={close}>
      <div
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label={t('nav:commandBar.label')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cmdk-input-row">
          <FeatherIcon name="search" size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            className="cmdk-input"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-autocomplete="list"
            aria-activedescendant={results[active] ? optionId(active) : undefined}
            aria-label={t('nav:commandBar.placeholder')}
            placeholder={t('nav:commandBar.placeholder')}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onInputKey}
          />
          <kbd className="cmdk-key">Esc</kbd>
        </div>
        <ul id="cmdk-list" className="cmdk-list" role="listbox" aria-label={t('nav:commandBar.label')}>
          {results.map((r, i) => (
            <li
              key={r.to}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={`cmdk-option${i === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(r)}
            >
              <span className="cmdk-option-label">{r.label}</span>
              {r.where && <span className="cmdk-option-where">{r.where}</span>}
            </li>
          ))}
        </ul>
        {results.length === 0 && (
          <p className="cmdk-empty">{t('nav:commandBar.empty', { query })}</p>
        )}
        <div className="cmdk-foot" aria-hidden="true">
          <span><kbd className="cmdk-key">↑</kbd><kbd className="cmdk-key">↓</kbd>{t('nav:commandBar.hintMove')}</span>
          <span><kbd className="cmdk-key">↵</kbd>{t('nav:commandBar.hintOpen')}</span>
          <span><kbd className="cmdk-key">Esc</kbd>{t('nav:commandBar.hintClose')}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CommandBar;
